import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { isAxiosError } from 'axios';
import { FormApi } from 'final-form';
import React, { useCallback, useState, useContext } from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import * as api from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Option } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { buildRestaurantApiError } from 'apis/restaurant-api/utils';
import { WoltModal } from 'common/components';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { getModifiedFieldsForTracking, useCreateOptionsTracking } from 'modules/menu/hooks/telemetryHooks';
import { OptionFormData } from 'modules/menu/types';

import AddOptionValueButton from './OptionForm/AddOptionValueButton';
import OptionForm from './OptionForm/OptionForm';
import OptionFormFields from './OptionForm/OptionFormFields';
import OptionValueAdvancedDetailsModal from './OptionForm/OptionValueAdvancedDetailsModal';
import { MenuPermissionsContext } from '../../components/MenuPermissionsContext';
import { buildCreateOptionFormData, buildOptionCreatePayload, sanitizeOptionFormData } from '../utils';

type Props = {
  country: Country;
  menuId: string;
  initialFormValues?: OptionFormData;
  onOptionCreated: (option: Option) => void;
  primaryLanguage: Language;
  isOpen: boolean;
  onClose: () => void;
};

const StyledWoltModal = styled(WoltModal)`
  border-radius: 8px;
  background: ${cssThemedValue('surfaceMain')};
  max-height: 80vh;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0 24px;
`;

const OptionValueActions = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin-block-start: 12px;
  margin-block-end: 12px;
`;

const AdvancedDetailsButton = styled(TextButton)``;

const FormError = styled(Alert).attrs({ variant: 'error' })`
  margin-block-end: 16px;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;

  & > button + button {
    margin-inline-start: 16px;
  }
`;

const OptionModal = ({ menuId, onOptionCreated, primaryLanguage, initialFormValues, isOpen, onClose }: Props) => {
  const { currency } = useContext(CurrencyContext);
  const menuFeatures = useContext(MenuFeaturesContext);
  const menuPermissions = useContext(MenuPermissionsContext);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [advandedDetailsModalIsOpen, setAdvancedDetailsModalIsOpen] = useState(false);
  const handleAdvancedDetailsModalClose = useCallback(() => setAdvancedDetailsModalIsOpen(false), []);
  const trackCreateOption = useCreateOptionsTracking();
  const handleSubmit = async (formData: OptionFormData, form: FormApi<OptionFormData, Partial<OptionFormData>>) => {
    setSubmitting(true);
    setSubmitError(null);

    let option: Option;
    try {
      option = await api.createOption(
        menuId,
        buildOptionCreatePayload(primaryLanguage, currency, menuId, sanitizeOptionFormData(formData)),
      );
      trackCreateOption(option.id, getModifiedFieldsForTracking<OptionFormData>(form.getState()));
    } catch (error) {
      if (!isAxiosError(error)) {
        throw error;
      }
      const restaurantApiError = buildRestaurantApiError(error);
      setSubmitting(false);
      setSubmitError(restaurantApiError.apiUiMessage || 'errors.unknown-error-retry-contact');
      return;
    }

    setSubmitting(false);
    onOptionCreated(option);
    onClose();
  };

  return (
    <MenuPermissionsContext.Provider value={menuPermissions}>
      <MenuFeaturesContext.Provider value={menuFeatures}>
        <StyledWoltModal isOpen={isOpen} onRequestClose={onClose}>
          <ModalHeader onCloseClick={onClose}>
            <FormattedMessage id="menu-option.add-modal-header" />
          </ModalHeader>
          <OptionForm
            initialValues={initialFormValues || buildCreateOptionFormData(primaryLanguage)}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit: handleFormElementSubmit }) => (
              <StyledForm onSubmit={handleFormElementSubmit}>
                <StyledModalContent>
                  {/*
                    We are gradually sunsetting Switch options so hide the Switch option type in the new option creation modal
                  */}
                  <OptionFormFields allowChangingOptionType hideSwitchOptionType />
                  <OptionValueActions>
                    <Field<OptionFormData['type']> name="type">
                      {({ input: { value } }) =>
                        (value === 'choice' || value === 'multi_choice') && <AddOptionValueButton />
                      }
                    </Field>
                    <AdvancedDetailsButton
                      type="button"
                      onClick={() => {
                        setAdvancedDetailsModalIsOpen(true);
                      }}
                    >
                      <FormattedMessage id="menu-option.edit-advanced-details" />
                    </AdvancedDetailsButton>
                  </OptionValueActions>
                  {submitError && (
                    <FormError>
                      <FormattedMessage id={submitError} />
                    </FormError>
                  )}
                </StyledModalContent>
                <StyledFooter>
                  <Button variant="lightBlue" size="small" onClick={onClose}>
                    <FormattedMessage id="state.cancel" />
                  </Button>
                  <Button type="submit" disabled={submitting} loading={submitting} variant="blue" size="small">
                    <FormattedMessage id="menu-option.add-option-submit" />
                  </Button>
                </StyledFooter>
                <OptionValueAdvancedDetailsModal
                  isOpen={advandedDetailsModalIsOpen}
                  onClose={handleAdvancedDetailsModalClose}
                />
              </StyledForm>
            )}
          </OptionForm>
        </StyledWoltModal>
      </MenuFeaturesContext.Provider>
    </MenuPermissionsContext.Provider>
  );
};

export default OptionModal;
